<script setup>
import { ref, computed, provide } from 'vue';
import { Virtualizer } from 'virtua/vue';
import { useBreakpoints } from '@vueuse/core';
import { useMapGetter } from 'dashboard/composables/store';
import { useChatListKeyboardEvents } from 'dashboard/composables/chatlist/useChatListKeyboardEvents';
import ConversationItem from './ConversationItem.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import IntersectionObserver from 'dashboard/components/IntersectionObserver.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import {
  Empty,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from 'next/polaris/ui/empty';

import wootConstants from 'dashboard/constants/globals';

const props = defineProps({
  conversationList: { type: Array, default: () => [] },
  isLoading: { type: Boolean, default: false },
  showEndOfListMessage: { type: Boolean, default: false },
  label: { type: String, default: '' },
  teamId: { type: [String, Number], default: 0 },
  foldersId: { type: [String, Number], default: 0 },
  conversationType: { type: String, default: '' },
  showAssignee: { type: Boolean, default: false },
  isOnExpandedLayout: { type: Boolean, default: false },
});

const emit = defineEmits(['loadMore']);

const conversationListRef = ref(null);
const virtualListRef = ref(null);
const isContextMenuOpen = ref(false);

provide('contextMenuElementTarget', virtualListRef);

const breakpoints = useBreakpoints({
  lg: wootConstants.LARGE_SCREEN_BREAKPOINT,
});
const isLgScreen = breakpoints.greaterOrEqual('lg');
const showExpandedCards = computed(
  () => props.isOnExpandedLayout && isLgScreen.value
);

// Same rule the expanded row uses (ConversationItem.vue) to show/hide its inbox column: mirrored
// here, outside the virtualizer, so the header's "Inbox" column only appears when the rows have one.
const activeInbox = useMapGetter('getSelectedInbox');
const inboxesList = useMapGetter('inboxes/getInboxes');
const showInboxColumn = computed(
  () => !activeInbox.value && inboxesList.value.length > 1
);

useChatListKeyboardEvents(conversationListRef);

const intersectionObserverOptions = computed(() => ({
  root: conversationListRef.value,
  rootMargin: '100px 0px 100px 0px',
}));

const onContextMenuToggle = state => {
  isContextMenuOpen.value = state;
};

const loadMoreConversations = () => {
  emit('loadMore');
};

provide('toggleContextMenu', onContextMenuToggle);

defineExpose({ conversationListRef });
</script>

<template>
  <div class="flex min-h-0 flex-1 flex-col">
    <!-- Expanded layout only: the table's column-header row, kept out of the virtualizer below (it
    isn't one of its items, so it never enters virtua's height calculation) and out of the
    scrollable body (so it stays put while the list scrolls). Aligned with the same fixed-width
    columns as ConversationCardExpanded.vue's row. -->
    <div
      v-if="showExpandedCards"
      data-slot="expanded-list-header"
      class="grid flex-shrink-0 items-center gap-4 border-b border-solid border-polaris-border bg-polaris-background px-3 h-10 type-polaris-caption text-polaris-muted-foreground grid-cols-[minmax(0,2fr)_minmax(0,1fr)] overflow-hidden [scrollbar-gutter:stable]"
    >
      <div class="flex min-w-0 items-center">
        <span class="flex w-8 flex-shrink-0 items-center justify-center p-2">
          <span class="sr-only">
            {{ $t('CHAT_LIST.EXPANDED_TABLE.COLUMNS.SELECT') }}
          </span>
        </span>
        <span class="flex w-8 flex-shrink-0 items-center justify-center p-2">
          <span class="sr-only">
            {{ $t('CHAT_LIST.EXPANDED_TABLE.COLUMNS.PRIORITY') }}
          </span>
        </span>
        <span class="flex w-8 flex-shrink-0 items-center justify-center p-2">
          <span class="sr-only">
            {{ $t('CHAT_LIST.EXPANDED_TABLE.COLUMNS.ASSIGNEE') }}
          </span>
        </span>
        <span class="flex w-8 flex-shrink-0 items-center justify-center p-2">
          <span class="sr-only">
            {{ $t('CHAT_LIST.EXPANDED_TABLE.COLUMNS.STATUS') }}
          </span>
        </span>
        <span v-if="showInboxColumn" class="w-20 flex-shrink-0 truncate p-2">
          {{ $t('CHAT_LIST.EXPANDED_TABLE.COLUMNS.INBOX') }}
        </span>
        <span class="w-full max-w-20 flex-shrink-0 truncate p-2">
          {{ $t('CHAT_LIST.EXPANDED_TABLE.COLUMNS.ID') }}
        </span>
        <span class="w-10 flex-shrink-0 p-2" aria-hidden="true" />
        <span class="w-32 flex-shrink-0 truncate p-2">
          {{ $t('CHAT_LIST.EXPANDED_TABLE.COLUMNS.CONTACT') }}
        </span>
        <span class="min-w-0 flex-1 truncate p-2">
          {{ $t('CHAT_LIST.EXPANDED_TABLE.COLUMNS.MESSAGE') }}
        </span>
      </div>
      <div class="flex flex-shrink-0 items-center justify-end">
        <span class="w-[4.375rem] flex-shrink-0 truncate p-2 text-end">
          {{ $t('CHAT_LIST.EXPANDED_TABLE.COLUMNS.TIME') }}
        </span>
      </div>
    </div>
    <div
      ref="conversationListRef"
      class="flex-1 min-h-0 overflow-y-auto conversations-list"
      :class="{
        '!overflow-hidden': isContextMenuOpen,
        'px-2': !showExpandedCards,
        '[scrollbar-gutter:stable]': showExpandedCards,
      }"
    >
      <Empty
        v-if="!isLoading && !conversationList.length"
        class="h-full p-6 md:p-6"
      >
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Icon icon="i-lucide-message-circle" class="size-6" />
          </EmptyMedia>
          <EmptyTitle>{{ $t('CHAT_LIST.LIST.404') }}</EmptyTitle>
        </EmptyHeader>
      </Empty>
      <template v-else>
        <Virtualizer
          ref="virtualListRef"
          v-slot="{ item }"
          :data="conversationList"
        >
          <ConversationItem
            :source="item"
            :label="label"
            :team-id="teamId"
            :folders-id="foldersId"
            :conversation-type="conversationType"
            :show-assignee="showAssignee"
            :show-expanded="showExpandedCards"
          />
        </Virtualizer>
        <div v-if="isLoading" class="flex justify-center my-4">
          <Spinner class="text-n-brand" />
        </div>
        <p
          v-else-if="showEndOfListMessage"
          class="mb-0 type-polaris-caption text-polaris-muted-foreground border-t border-polaris-border px-4 py-3 text-center"
        >
          {{
            $t('CHAT_LIST.CONVERSATION_COUNT', {
              count: conversationList.length,
            })
          }}
        </p>
        <IntersectionObserver
          v-else
          :options="intersectionObserverOptions"
          @observed="loadMoreConversations"
        />
      </template>
    </div>
  </div>
</template>
